'use client';

import { useEffect } from 'react';

export const ADSENSE_CLIENT = 'ca-pub-2310914340001384';
const ADSENSE_SRC = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
const SCRIPT_ID = 'adsbygoogle-loader';

// Ads are NON-PERSONALISED for everyone: the site is used by children, the privacy policy
// promises it, and since May 2025 AdSense has no account-level switch for it any more
// ("Ad choice control" was removed from Privacy & messaging), so the tag must ask.
// Both documented mechanisms are set: the older `requestNonPersonalizedAds` flag (shows as
// `npa=1` on ad requests) and the newer privacy treatment on the loader (`ppt=1`).
// Either one alone is enough; both cost nothing. https://support.google.com/adsense/answer/7670312
type AdsByGoogle = unknown[] & { requestNonPersonalizedAds?: number };

/**
 * The adsbygoogle command queue, with the non-personalised flag set. Every push (the loader here
 * and each manual unit, see ToolSwitchAd) goes through this, so no ad request can ever go out
 * without `npa=1`, whichever runs first. Pushes made before adsbygoogle.js arrives are queued and
 * run when it loads.
 */
export function adsQueue(): AdsByGoogle {
  const w = window as unknown as { adsbygoogle?: AdsByGoogle };
  const queue: AdsByGoogle = (w.adsbygoogle = w.adsbygoogle || []);
  queue.requestNonPersonalizedAds = 1;
  return queue;
}

// Injects the AdSense loader AFTER hydration, never as a server-rendered tag.
//
// A server-rendered loader (even `defer`) can run before React hydrates; adsbygoogle
// then mutates the DOM and React throws #418 and client-renders the whole root (LCP/INP
// hit, and errorFilters.ts drops #418 so it never reaches the error board). Waiting for
// `load` plus an idle slot also covers Suspense boundaries that hydrate after this effect.
//
// The Consent Mode defaults stay a server-rendered inline script in app/layout.tsx, so
// they always run first. The nonce comes from that script's `nonce` PROPERTY: browsers
// blank the attribute after parsing (nonce-hiding), the property keeps the value.
export default function AdSenseLoader() {
  useEffect(() => {
    let idleHandle: number | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    const inject = () => {
      if (document.getElementById(SCRIPT_ID)) return;
      adsQueue();
      const consent = document.getElementById('google-analytics-consent') as HTMLScriptElement | null;
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = ADSENSE_SRC;
      script.crossOrigin = 'anonymous';
      script.setAttribute('data-privacy-treatments', 'disablePersonalization');
      if (consent?.nonce) script.nonce = consent.nonce;
      document.head.appendChild(script);
    };

    const schedule = () => {
      if ('requestIdleCallback' in window) {
        idleHandle = window.requestIdleCallback(inject, { timeout: 2000 });
      } else {
        timeoutHandle = setTimeout(inject, 1);
      }
    };

    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });

    return () => {
      window.removeEventListener('load', schedule);
      if (idleHandle !== undefined) window.cancelIdleCallback(idleHandle);
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    };
  }, []);

  return null;
}
